import {translate} from '@docusaurus/Translate';
import type {
  ConversationMessage,
  PageContext,
} from './assistantConversation';
import {
  MAX_QUESTION_LENGTH,
  isPageContext,
  isStoredMessage,
  restoreConversationMessages,
} from './assistantConversation';

export const HISTORY_STORAGE_KEY = 'folderspan.ai-docs-assistant.history.v1';
export const HISTORY_STORAGE_VERSION = 1;
export const MAX_HISTORY_CONVERSATIONS = 20;

export type ConversationRecord = {
  localId: string;
  sessionId: string | null;
  title: string;
  updatedAt: string;
  messages: ConversationMessage[];
  sourceContext: PageContext | null;
  draft: string;
};

export type HistoryStore = {
  version: number;
  conversations: ConversationRecord[];
};

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function conversationTitle(messages: ConversationMessage[]): string {
  const firstQuestion = messages.find(
    (message) => message.role === 'user' && message.content.trim().length > 0,
  );
  if (!firstQuestion) {
    return translate({message: '未命名会话'});
  }

  const compact = firstQuestion.content.trim().replace(/\s+/g, ' ');
  return compact.length > 36 ? `${compact.slice(0, 36)}…` : compact;
}

export function toConversationRecord(input: {
  localId: string;
  sessionId: string | null;
  messages: ConversationMessage[];
  sourceContext: PageContext | null;
  draft: string;
  updatedAt?: string;
}): ConversationRecord | null {
  if (!input.localId || input.messages.length === 0) {
    return null;
  }

  return {
    localId: input.localId,
    sessionId: input.sessionId,
    title: conversationTitle(input.messages),
    updatedAt: input.updatedAt ?? new Date().toISOString(),
    messages: input.messages.every(isStoredMessage) ? input.messages : restoreConversationMessages(input.messages),
    sourceContext: isPageContext(input.sourceContext) ? input.sourceContext : null,
    draft: input.draft.slice(0, MAX_QUESTION_LENGTH),
  };
}

function isConversationRecord(value: unknown): value is ConversationRecord {
  if (
    !isRecord(value) ||
    typeof value.localId !== 'string' ||
    value.localId.length === 0 ||
    (value.sessionId !== null && typeof value.sessionId !== 'string') ||
    typeof value.title !== 'string' ||
    typeof value.updatedAt !== 'string' ||
    !Array.isArray(value.messages) ||
    value.messages.length === 0 ||
    typeof value.draft !== 'string' ||
    (value.sourceContext !== null && !isPageContext(value.sourceContext))
  ) {
    return false;
  }

  const restored = restoreConversationMessages(value.messages);
  return restored.length === value.messages.length;
}

function normalizeHistory(value: unknown): ConversationRecord[] {
  if (
    !isRecord(value) ||
    value.version !== HISTORY_STORAGE_VERSION ||
    !Array.isArray(value.conversations)
  ) {
    return [];
  }

  const seen = new Set<string>();
  const conversations: ConversationRecord[] = [];

  for (const item of value.conversations) {
    if (!isConversationRecord(item) || seen.has(item.localId)) {
      continue;
    }

    const restored = toConversationRecord({
      ...item,
      messages: restoreConversationMessages(item.messages),
    });
    if (!restored) {
      continue;
    }

    seen.add(restored.localId);
    conversations.push(restored);
    if (conversations.length >= MAX_HISTORY_CONVERSATIONS) {
      break;
    }
  }

  return conversations.sort((left, right) => right.updatedAt.localeCompare(left.updatedAt));
}

export function readAssistantHistory(): ConversationRecord[] {
  try {
    const storedValue = window.localStorage.getItem(HISTORY_STORAGE_KEY);
    if (!storedValue) {
      return [];
    }

    return normalizeHistory(JSON.parse(storedValue) as unknown);
  } catch {
    return [];
  }
}

export function writeAssistantHistory(conversations: ConversationRecord[]): ConversationRecord[] {
  let next = conversations
    .filter((item, index, list) => list.findIndex((entry) => entry.localId === item.localId) === index)
    .sort((left, right) => right.updatedAt.localeCompare(left.updatedAt))
    .slice(0, MAX_HISTORY_CONVERSATIONS);

  for (;;) {
    try {
      const store: HistoryStore = {
        version: HISTORY_STORAGE_VERSION,
        conversations: next,
      };
      window.localStorage.setItem(HISTORY_STORAGE_KEY, JSON.stringify(store));
      return next;
    } catch {
      if (next.length === 0) {
        return [];
      }
      next = next.slice(0, -1);
    }
  }
}

export function upsertHistoryConversation(
  conversations: ConversationRecord[],
  record: ConversationRecord,
): ConversationRecord[] {
  const existing = conversations.find((item) => item.localId === record.localId);
  if (existing && existing.updatedAt === record.updatedAt) {
    return conversations.map((item) => (item.localId === record.localId ? record : item));
  }

  return [record, ...conversations.filter((item) => item.localId !== record.localId)].slice(
    0,
    MAX_HISTORY_CONVERSATIONS,
  );
}

export function removeHistoryConversation(
  conversations: ConversationRecord[],
  localId: string,
): ConversationRecord[] {
  return conversations.filter((item) => item.localId !== localId);
}
